import Image from "next/image"
import { HttpTypes } from "@medusajs/types"

import { CartDropdown, Navbar } from "@/components/cells"
import { UserDropdown } from "@/components/cells/UserDropdown/UserDropdown"
import CountrySelector from "@/components/molecules/CountrySelector/CountrySelector"
import LocalizedClientLink from "@/components/molecules/LocalizedLink/LocalizedLink"
import { NotificationButton } from "@/components/molecules/NotificationButton/NotificationButton"
import { listNotifications } from "@/lib/data/notifications"
import { listCategories } from "@/lib/data/categories"
import { listRegions } from "@/lib/data/regions"
import { retrieveCustomer } from "@/lib/data/customer"
import { ParentCategoryLinks } from "@/components/molecules/ParentCategoryLinks/ParentCategoryLinks"
import { getStorefront } from "@/lib/data/storefront"

export const Header = async ({ locale } : {
  locale: string
}) => {
  const user = await retrieveCustomer().catch(() => null)
  const isLoggedIn = Boolean(user)
  const { unread } = isLoggedIn ? await listNotifications() : { unread: 0 }

  const regions = await listRegions()
  const { identity } = await getStorefront()

  const { categories, parentCategories } = (await listCategories({ query: { include_ancestors_tree: true } })) as {
    categories: HttpTypes.StoreProductCategory[]
    parentCategories: HttpTypes.StoreProductCategory[]
  }
  return (
    <header data-testid="header">
      <div className="flex gap-3 py-2 lg:px-8 px-4 md:px-5" data-testid="header-top">
        <div className="flex items-center lg:w-1/3">
          {/* قائمة الأقسام الجانبية للهاتف أُزيلت: الأقسام في شاشة البحث، ومساحتها لاسم المتجر. */}
          <ParentCategoryLinks
            parentCategories={parentCategories}
            categories={categories}
          />
        </div>
        <div className="flex min-w-0 flex-1 lg:flex-none lg:justify-center lg:w-1/3 items-center pl-2 lg:pl-0">
          <LocalizedClientLink href="/" className="flex min-w-0 items-center gap-2 text-2xl font-bold" data-testid="header-logo-link">
            {identity.logo_url && (
              <img src={identity.logo_url} alt="" className="h-8 w-auto max-w-[3.5rem] object-contain sm:h-9" />
            )}
            <span dir="rtl" className="block min-w-0 text-sm font-bold leading-tight sm:text-xl">
              {identity.name}
              {/* الاسم كاملاً؛ على الهاتف ينزل سطراً بدل أن يُقصّ أو يدفع الأزرار خارج الشاشة. */}
              {identity.name_rest && <> <span className="font-normal text-gray-500">{identity.name_rest}</span></>}
            </span>
          </LocalizedClientLink>
        </div>
        <div className="flex shrink-0 items-center justify-end gap-2 lg:gap-4 lg:w-1/3 py-2" data-testid="header-actions">
          <CountrySelector regions={regions} />
          {isLoggedIn && <NotificationButton unread={unread} />}
          {isLoggedIn ? (
            <UserDropdown
              isLoggedIn={isLoggedIn}
              name={[user?.first_name, user?.last_name].filter(Boolean).join(" ") || user?.email}
              firstName={user?.first_name}
            />
          ) : (
            <>
              <LocalizedClientLink
                href="/login"
                aria-label="دخول أو حساب جديد"
                className="flex items-center gap-1.5 rounded-full border border-white/50 px-2.5 py-1.5 text-sm font-medium text-white hover:bg-white/10 sm:px-4"
                data-testid="header-login"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4" fill="currentColor">
                  <circle cx="12" cy="8" r="4" />
                  <path d="M4 20c0-4 3.6-6.5 8-6.5s8 2.5 8 6.5z" />
                </svg>
                <span>دخول</span>
              </LocalizedClientLink>
            </>
          )}
          <CartDropdown />
        </div>
      </div>
      <Navbar categories={categories} parentCategories={parentCategories} />
    </header>
  )
}
